import { Button, Grid } from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Accordion from '../../../../components/Accordion';
import BooleanCheckbox from '../../../../components/fields/BooleanCheckbox';
import OutlinedDateTimePickerField from '../../../../components/fields/OutlinedDateTimePickerField';
import OutlinedInputField from '../../../../components/fields/OutlinedInputField';
import DealerSelection from './DealerSelection';
import SelectedDealersList from './SelectedDealersList';
import TagSelection from './TagSelection';
import useInviteQuoteData from './hooks/useInviteQuoteData';
import useInviteQuoteSelection from './hooks/useInviteQuoteSelection';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        actionButton: {
            width: 240,
            maxWidth: '100%',
            marginLeft: 'auto',
            marginRight: 'auto',
            [theme.breakpoints.up('sm')]: {
                marginLeft: 0,
                marginRight: 0,
            },
        },
        actionButtonContainer: {
            display: 'flex',
            justifyContent: 'center',
            [theme.breakpoints.up('sm')]: {
                justifyContent: 'flex-end',
            },
        },
    })
);

type CreationFormValues = {
    remarks: string;
    hideVehicleNo: boolean;
    startPeriod: Date | null;
    endPeriod: Date | null;
};

export type CreationFormProps = {
    purchaseTransactionId?: string;
};

const CreationForm = () => {
    const { t } = useTranslation('purchaseTransactionsPage');
    const { values, setFieldValue } = useFormikContext<CreationFormValues>();
    const { endPeriod, startPeriod } = values;
    const styles = useStyles();
    const { dealers, tags } = useInviteQuoteData();
    const { availableDealers, onDealerSelectionChange, onTagSelectionChange, selectedDealers } =
        useInviteQuoteSelection({
            dealers,
            ignoreInSessionFilter: false,
            inSessionDealerIds: [],
            tags,
        });

    useEffect(() => {
        if (endPeriod && (!startPeriod || !dayjs(endPeriod).isAfter(dayjs(startPeriod)))) {
            setFieldValue('endPeriod', null);
        }
    }, [endPeriod, setFieldValue, startPeriod]);

    return (
        <Accordion title={t('purchaseTransactionsPage:accordionTitle.createQuotation')} defaultExpanded>
            <Grid spacing={2} container>
                <Grid lg={12} item>
                    <BooleanCheckbox
                        color="secondary"
                        label={t('quotation.mainInfo.hideVehicleNo')}
                        name="hideVehicleNo"
                        style={{ color: 'red' }}
                    />
                </Grid>
                <Grid lg={6} md={6} sm={12} xs={12} item>
                    <OutlinedDateTimePickerField
                        color="secondary"
                        label={t('quotation.mainInfo.startDate')}
                        name="startPeriod"
                        placeholder={t('common:formats.dateTimePickerFormat')}
                        clearable
                        disablePast
                        required
                    />
                </Grid>
                <Grid lg={6} md={6} sm={12} xs={12} item>
                    <OutlinedDateTimePickerField
                        color="secondary"
                        label={t('quotation.mainInfo.endDate')}
                        name="endPeriod"
                        placeholder={t('common:formats.dateTimePickerFormat')}
                        clearable
                        disablePast
                    />
                </Grid>
                <Grid xs={12} item>
                    <OutlinedInputField
                        color="secondary"
                        label={t('quotation.mainInfo.remarks')}
                        name="remarks"
                        placeholder={t('quotation.mainInfo.enterDescription')}
                        multiline
                    />
                </Grid>
                <Grid lg={6} md={6} sm={12} xs={12} item>
                    <TagSelection onSelectionChange={onTagSelectionChange} tags={tags} />
                </Grid>
                <Grid lg={6} md={6} sm={12} xs={12} item>
                    <DealerSelection dealers={availableDealers} onSelectionChange={onDealerSelectionChange} />
                </Grid>
                <Grid xs={12} item>
                    <SelectedDealersList selectedDealers={selectedDealers} displayDealerTagNames />
                </Grid>
                <Grid className={styles.actionButtonContainer} xs={12} container item>
                    <Button className={styles.actionButton} color="secondary" type="submit" variant="contained">
                        {t(`purchaseTransactionsPage:quotation.sendForQuotation`)}
                    </Button>
                </Grid>
            </Grid>
        </Accordion>
    );
};

export default CreationForm;
